<template #default>
    <form
        v-if="status.detail && !loading"
        @submit.prevent="candidateJobUUID ? requestUpdate() : requestCreate()"
    >
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-briefcase-outline mr-1"></i>
                    Basic Information
                </h6>
            </div>
            <div class="col-md-12">
                <b-form-group
                    :label="$t('label.name')"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.name"
                        :class="{ 'is-invalid': submitted && !$v.createForm.name.required }"
                        autocomplete="off"
                    />
                    <b-form-invalid-feedback v-if="submitted && !$v.createForm.name.required" class="text-right">
                        {{ $t("message.requiredX", [$t("label.name")]) }}
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    :label="$t('module.company')"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.company"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="companyDepartmentOfficeDdl"
                        :searchable="true"
                        :allow-empty="false"
                        class="cursor-pointer"
                        @input="() => {
                            createForm.department = null;
                            createForm.office = null;
                        }"
                    />
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    :label="$t('module.department')"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.department"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="getDepartmentOptions()"
                        :searchable="true"
                        :allow-empty="false"
                        class="cursor-pointer"
                        :disabled="!createForm.company"
                        @input="() => {
                            createForm.office = null;
                        }"
                    />
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    :label="$t('module.office')"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.office"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="getOfficeOptions()"
                        :searchable="true"
                        :allow-empty="false"
                        class="cursor-pointer"
                        :disabled="!createForm.company"
                    />
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    label="Location"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.location"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    label="Openings"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.openings"
                        :class="{ 'is-invalid': submitted && $v.createForm.openings.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.openings.decimal">{{ $t("message.invalidX", ["Openings"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    label="Intern"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-checkbox
                        v-model="createForm.isIntern"
                        switch
                        class="mt-2"
                    />
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    label="Fresh Graduate"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-checkbox
                        v-model="createForm.isFreshGraduate"
                        switch
                        class="mt-2"
                    />
                </b-form-group>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-currency-usd mr-1"></i>
                    Remuneration
                </h6>
            </div>
            <div class="col-md-12">
                <b-form-group
                    label="Allowance"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.allowance"
                        :class="{ 'is-invalid': submitted && $v.createForm.allowance.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.allowance.decimal">{{ $t("message.invalidX", ["Allowance"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    label="Minimum Salary"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.minSalary"
                        :class="{ 'is-invalid': submitted && $v.createForm.minSalary.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.minSalary.decimal">{{ $t("message.invalidX", ["Minimum Salary"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    label="Maximum Salary"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.maxSalary"
                        :class="{ 'is-invalid': submitted && $v.createForm.maxSalary.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.maxSalary.decimal">{{ $t("message.invalidX", ["Maximum Salary"]) }}</span>
                    </div>
                </b-form-group>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-bullhorn mr-1"></i>
                    Publish
                </h6>
            </div>
            <div class="col-md-12">
                <b-form-group
                    label="Published"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-checkbox
                        v-model="createForm.isPublished"
                        switch
                        class="mt-2"
                    />
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    label="Closing Date"
                    label-cols="4"
                    label-align="left"
                >
                    <DatePicker
                        v-model="createForm.closingDate"
                        placeholder="yyyy-mm-dd"
                        class="cursor-pointer"
                        :config="{
                            format: 'YYYY-MM-DD',
                            useCurrent: false,
                            icons: {
                                time: 'mdi mdi-clock-outline',
                                date: 'mdi mdi-calendar',
                                up: 'mdi mdi-chevron-up',
                                down: 'mdi mdi-chevron-down',
                                previous: 'mdi mdi-chevron-left',
                                next: 'mdi mdi-chevron-right',
                                today: 'mdi mdi-calendar-check',
                                clear: 'mdi mdi-delete-outline',
                                close: 'mdi mdi-close',
                            },
                        }"
                    />
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    :label="$t('label.description')"
                    label-cols="4"
                    label-align="left"
                >
                    <ckeditor
                        v-model="createForm.description"
                        :editor="editor"
                        :config="editorConfig"
                        @ready="onEditorReady"
                    />
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    :label="$t('label.tag')"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.tags"
                        :options="[]"
                        :multiple="true"
                        :taggable="true"
                        :placeholder="$t('message.pressEnterToAddX', [])"
                        :tag-placeholder="$t('message.pressEnterToAddX', [$t('label.hashtag')])"
                        :show-options="false"
                        :show-no-options="false"
                        v-on:tag="addTag"
                    />
                </b-form-group>
            </div>
        </div>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button
                type="button"
                variant="danger"
                class="mr-2 col-2"
                @click="$emit('done')"
            >{{ $t('label.cancel') }}</b-button>
            <b-button
                type="submit"
                variant="info"
                class="col-2"
                :disabled="!status.save"
            >
                <span v-if="status.save">
                    {{ candidateJobUUID ? $t('label.updateX', []) : $t('label.createX', []) }}
                </span>
                <div v-else>
                    <b-spinner small />
                </div>
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-5 p-5">
        <b-spinner class="m-5" variant="info" role="status"></b-spinner>
    </div>
</template>
<script>
import CKEditor from '@ckeditor/ckeditor5-vue';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import DatePicker from 'vue-bootstrap-datetimepicker';
import Multiselect from "vue-multiselect";
import moment from "moment";
import { required, decimal } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { candidateJobMethods, lookupMethods } from "@/state/api";
import { CANDIDATE_JOB_MODULE } from "@/state/api/candidateJob.state";

export default {
    props: {
        candidateJobName: {
            type: String,
            required: false,
        },
        candidateJobUUID: {
            type: String,
            required: false,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    validations() {
        return {
            createForm: {
                name: { required },
                openings: { decimal },
                allowance: { decimal },
                minSalary: { decimal },
                maxSalary: { decimal },
            },
        };
    },
    components: { DatePicker, Multiselect, ckeditor: CKEditor.component },
    async created() {
        await this.lookupCompanyDepartmentOffice({});
        await this.getCandidateJobData();

        this.loading = false;
    },
    data() {
        return {
            submitted: false,
            createForm: this.defaultForm(),
            loading: !!this.candidateJobUUID,
            editor: ClassicEditor,
            editorConfig: {
                removePlugins: [
                    'ImageCaption'
                ],
                toolbar: [
                    'selectAll',
                    'heading',
                    'bold',
                    'italic',
                    'blockQuote',
                    'bulletedList',
                    'numberedList',
                    'undo',
                    'redo',
                    'outdent',
                    'indent',
                    // 'imageUpload',
                    // 'imageStyle:full',
                    // 'imageStyle:side',
                    // 'imageTextAlternative',
                ],
            },
            tagOptions: [],
        };
    },
    methods: {
        ...candidateJobMethods,
        ...lookupMethods,
        defaultForm() {
            return {
                uuid: "",
                company: null,
                department: null,
                office: null,
                name: this.candidateJobName || "",
                location: "",
                openings: null,
                allowance: null,
                minSalary: null,
                maxSalary: null,
                closingDate: null,
                description: "",
                tags: [],
                isIntern: false,
                isFreshGraduate: false,
                isPublished: false,
            };
        },
        async getCandidateJobData() {
            if (this.candidateJobUUID) {
                await this.getCandidateJob({ uuid: this.candidateJobUUID })
                    .then((result) => {
                        if (result?.success) {
                            const tags = result.data.tags ?? [];

                            this.createForm = {
                                ...this.data,
                                tags: tags,
                                company: this.data.company ? {
                                    value: this.data.company.uuid,
                                    text: this.data.company.name,
                                } : null,
                                department: this.data.department ? {
                                    value: this.data.department.uuid,
                                    text: this.data.department.name,
                                } : null,
                                office: this.data.office ? {
                                    value: this.data.office.uuid,
                                    text: this.data.office.name,
                                } : null,
                                isIntern: this.data.isIntern ? true : false,
                                isFreshGraduate: this.data.isFreshGraduate ? true : false,
                                isPublished: this.data.isPublished ? true : false,
                            };
                        }
                    });
            }
        },
        requestCreate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    companyUUID: this.createForm.company?.value,
                    departmentUUID: this.createForm.department?.value,
                    officeUUID: this.createForm.office?.value,
                    isIntern: this.createForm.isIntern ? 1 : 0,
                    isFreshGraduate: this.createForm.isFreshGraduate ? 1 : 0,
                    isPublished: this.createForm.isPublished ? 1 : 0,
                    closingDate: this.createForm.closingDate ? moment(this.createForm.closingDate).format('YYYY-MM-DD') : null,
                };
                this.createCandidateJob({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        requestUpdate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    companyUUID: this.createForm.company?.value,
                    departmentUUID: this.createForm.department?.value,
                    officeUUID: this.createForm.office?.value,
                    isIntern: this.createForm.isIntern ? 1 : 0,
                    isFreshGraduate: this.createForm.isFreshGraduate ? 1 : 0,
                    isPublished: this.createForm.isPublished ? 1 : 0,
                    closingDate: this.createForm.closingDate ? moment(this.createForm.closingDate).format('YYYY-MM-DD') : null,
                };
                this.updateCandidateJob({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.$v.$reset();
        },
        onEditorReady(editor) {
            // console.log('Available toolbar items:', Array.from(editor.ui.componentFactory.names()).sort());
            editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
                return {
                    upload: () => loader.file.then((file) => {
                        return this.uploadCandidateJobImage({data: {file}})
                            .then((result) => {
                                return {
                                    default: result?.data,
                                };
                            });
                    }),
                    abort: () => {},
                };
            };
        },
        addTag(newTag) {
            const tag = newTag.trim();
            if (tag && !this.createForm.tags.includes(tag)) this.createForm.tags.push(tag);
        },
        getDepartmentOptions() {
            const company = this.companyDepartmentOfficeDdl.find(company => company.value == this.createForm.company?.value);
            return company?.departments || [];
        },
        getOfficeOptions() {
            const company = this.companyDepartmentOfficeDdl.find(company => company.value == this.createForm.company?.value);
            return company?.offices || [];
        },
    },
    computed: {
        ...mapState(CANDIDATE_JOB_MODULE, ['status']),
        data() {
            return this.$store.state.api.candidateJob.details;
        },
        companyDepartmentOfficeDdl() {
            return this.$store.state.api.lookup.companyDepartmentOfficeDdl || [];
        },
    },
}
</script>
